import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { theme } from '../../theme';
import { getApiUrl } from '../../config';

interface TagItem {
  name: string;
  count: number;
}

interface PublicStats {
  totalMatches: number;
  mvpCount: number;
  totalGoals: number;
}

interface PersonalStatsCardProps {
  userId: string;
}

export default function PersonalStatsCard({ userId }: PersonalStatsCardProps) {
  const navigate = useNavigate();
  const [stats, setStats] = useState<PublicStats | null>(null);
  const [topTags, setTopTags] = useState<TagItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const [statsRes, tagsRes] = await Promise.all([
          fetch(`${getApiUrl('/api/users')}/${userId}/public-stats`),
          fetch(`${getApiUrl('/api/users')}/${userId}/public-tags-summary`),
        ]);

        const statsJson = statsRes.ok ? await statsRes.json() : null;
        const tagsJson = tagsRes.ok ? await tagsRes.json() : null;

        if (cancelled) return;

        if (statsJson) {
          setStats({
            totalMatches: statsJson.totalMatches ?? 0,
            mvpCount: statsJson.mvpCount ?? 0,
            totalGoals: statsJson.totalGoals ?? 0,
          });
        }
        if (tagsJson?.topPositive) {
          setTopTags(tagsJson.topPositive.slice(0, 3));
        }
      } catch {
        // silent fail — card just won't render
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [userId]);

  if (loading) return null;
  if (!stats) return null;

  const hasMatches = stats.totalMatches > 0;
  const hasTags = topTags.length > 0;
  if (!hasMatches && !hasTags) return null;

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => navigate('/profile')}
      onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && navigate('/profile')}
      style={{
        marginTop: 16,
        padding: 16,
        borderRadius: theme.borderRadius.card,
        border: `1px solid ${theme.colors.border}`,
        backgroundColor: theme.colors.surface,
        cursor: 'pointer',
        transition: 'transform 0.15s ease, box-shadow 0.15s ease',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-1px)';
        e.currentTarget.style.boxShadow = theme.colors.shadow;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = 'none';
      }}
    >
      <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontSize: 11,
              fontWeight: 600,
              color: theme.colors.textSecondary,
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              marginBottom: 6,
            }}
          >
            Tu vitrina
          </div>
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            {hasMatches && (
              <StatBlock icon="⚽" value={stats.totalMatches} label="partidos" />
            )}
            {stats.mvpCount > 0 && (
              <StatBlock icon="🏆" value={stats.mvpCount} label={stats.mvpCount === 1 ? 'MVP' : 'MVPs'} />
            )}
            {stats.totalGoals > 0 && (
              <StatBlock icon="🥅" value={stats.totalGoals} label={stats.totalGoals === 1 ? 'gol' : 'goles'} />
            )}
          </div>
        </div>

        {hasTags && (
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                fontSize: 11,
                fontWeight: 600,
                color: theme.colors.textSecondary,
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                marginBottom: 6,
              }}
            >
              Tus top tags
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {topTags.map((tag) => (
                <div
                  key={tag.name}
                  style={{
                    fontSize: 13,
                    fontWeight: 600,
                    color: theme.colors.text,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                  title={tag.name}
                >
                  {tag.name}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <div
        style={{
          marginTop: 12,
          textAlign: 'right',
          fontSize: 12,
          fontWeight: 600,
          color: theme.colors.primary,
        }}
      >
        Ver perfil completo →
      </div>
    </div>
  );
}

function StatBlock({ icon, value, label }: { icon: string; value: number; label: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
      <span style={{ fontSize: 18 }}>{icon}</span>
      <span style={{ fontSize: 22, fontWeight: 700, color: theme.colors.text }}>{value}</span>
      <span style={{ fontSize: 12, color: theme.colors.textSecondary }}>{label}</span>
    </div>
  );
}
